import { useState, useMemo, useCallback, useEffect, useRef, memo } from 'react';
import { isPrimaryMod } from '@/utils/input';
import {
  Text,
  Input,
  Button,
  ToggleButton,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Skeleton,
  SkeletonItem,
  Spinner,
  Link,
  Field,
  mergeClasses,
  createTableColumn,
  useTableFeatures,
  useTableColumnSizing_unstable,
  type TableColumnDefinition,
  type TableColumnSizingOptions,
} from '@coreai/fluentui-react';
import {
  SearchRegular,
  AddRegular,
  ArrowClockwiseRegular,
  EditRegular,
  DeleteRegular,
  DismissRegular,
  CheckmarkRegular,
  EyeRegular,
  EyeOffRegular,
  Braces48Regular,
  CopyRegular,
  ErrorCircle12Filled,
} from '@fluentui/react-icons';
import { useEnvironmentSettings, useUpdateEnvironmentSettings } from '@/hooks/useEnvironmentSettings';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { TagSwitch, type TagSwitchOption } from '@/components/common/TagSwitch';
import { useTelemetry } from '@/hooks/useTelemetry';
import { useEnvironmentVariablesTabStyles } from './EnvironmentVariablesTab.styles';
import {
  type EditableSetting,
  type ValidationError,
  COPIED_FLASH_MS,
  MASK,
  POSIX_KEY_HINT,
  ROW_FLASH_MS,
  buildPutSettings,
  buildValidationErrors,
  computeChangeCounts,
  formatReference,
  isModified,
  resetItemsAfterSave,
  settingsToEditable,
} from '@/utils/settings';
import type { ApiErrorWithStatus } from '@/services/environmentSettingsApi';

// =============================================================================
// TYPES
// =============================================================================

interface EnvironmentVariablesTabProps {
  projectName: string;
  appName: string;
}

/**
 * Classification of a save failure surfaced to the user. The kind drives the
 * MessageBar intent, title copy, body copy, and which actions are offered.
 *
 *  - `conflict`     — ARM still applying a prior save (transient 502 / 409).
 *                     Use warning intent + Retry. The runtime backend writes
 *                     env vars by re-deploying the underlying ACA container
 *                     app; rapid successive writes hit this window.
 *  - `unauthorized` — 401. Sign-in expired; Retry is pointless until reauth.
 *  - `forbidden`    — 403. Permissions issue; Retry is pointless.
 *  - `generic`      — anything else (e.g., 500). Show the sanitized error
 *                     message and offer Retry.
 */
type SaveErrorKind = 'conflict' | 'unauthorized' | 'forbidden' | 'generic';

/** Title copy by error kind (single source of truth, kept out of JSX). */
const SAVE_ERROR_TITLES: Record<SaveErrorKind, string> = {
  conflict: 'A previous save is still applying',
  unauthorized: 'You need to sign in again',
  forbidden: "You don't have permission to update settings",
  generic: 'Failed to save environment variables',
};

/** Whether this error class is worth offering a Retry button for. */
const SAVE_ERROR_OFFERS_RETRY: Record<SaveErrorKind, boolean> = {
  conflict: true,
  unauthorized: false,
  forbidden: false,
  generic: true,
};

// Column ids for the resizable Table. Defined at module scope so the array
// identity is stable across renders (otherwise `useTableFeatures` would reset
// its sizing state every render).
type EnvColumnId = 'name' | 'value' | 'actions';
const COLUMNS: TableColumnDefinition<EditableSetting>[] = [
  createTableColumn<EditableSetting>({ columnId: 'name' }),
  createTableColumn<EditableSetting>({ columnId: 'value' }),
  createTableColumn<EditableSetting>({ columnId: 'actions' }),
];
// Sensible default widths in px. The user can drag the column dividers to
// resize; `minWidth` prevents columns from being shrunk into uselessness.
const COLUMN_SIZING: TableColumnSizingOptions = {
  name: { defaultWidth: 280, minWidth: 160, idealWidth: 280 },
  value: { defaultWidth: 480, minWidth: 200, idealWidth: 480 },
  actions: { defaultWidth: 120, minWidth: 96, idealWidth: 120 },
};

const environmentViews: readonly TagSwitchOption<'production' | 'draft'>[] = [
  { value: 'production', label: 'Production' },
  { value: 'draft', label: 'Draft' },
];

// =============================================================================
// COMPONENT
// =============================================================================

export const EnvironmentVariablesTab = ({ projectName, appName }: EnvironmentVariablesTabProps) => {
  const styles = useEnvironmentVariablesTabStyles();
  const { trackEvent, trackException } = useTelemetry();

  // Draft environment settings are gated behind a feature flag. When off, the
  // Production/Draft toggle is hidden and every read/write targets Production.
  const draftSettingsEnabled = useFeatureFlag('enableDraftSettings');

  // Draft vs Production slot. Defaults to Production; switching to Draft reads
  // (and writes) the draft environment-settings slot. Threaded into the read
  // and update hooks so the query key is mode-scoped and refetches on toggle.
  // Forced to Production whenever the feature flag is off.
  const [isDraftModeSelection, setIsDraftMode] = useState(false);
  const isDraftMode = useMemo(() => draftSettingsEnabled && isDraftModeSelection, [draftSettingsEnabled, isDraftModeSelection]);

  // Data hooks
  const { data: prodSettings } = useEnvironmentSettings(projectName, appName, /* isDraftMode */ false);
  const { data: settings, isLoading, isFetching, isError, error, refetch } = useEnvironmentSettings(projectName, appName, isDraftMode);
  const updateMutation = useUpdateEnvironmentSettings(projectName, appName, isDraftMode, /* prodSettings */ prodSettings ?? []);

  // Local state
  const [items, setItems] = useState<EditableSetting[]>([]);
  const [filterText, setFilterText] = useState('');
  const [showValues, setShowValues] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editValue, setEditValue] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [deletePopoverId, setDeletePopoverId] = useState<string | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  // Ids of rows whose name input has been blurred at least once. We use this
  // to gate display of regex/duplicate errors in the add/edit overlays so the
  // user isn't scolded while still typing.
  const [touchedNameIds, setTouchedNameIds] = useState<Set<string>>(() => new Set());
  // Snapshot of a row's name/value when an edit began. Used to restore on
  // Cancel since the edit-flow live-syncs both fields into `items` (so that
  // duplicate validation can run against the candidate name in real time,
  // and so the footer Save reflects the row's current edit buffer).
  const [editOriginalName, setEditOriginalName] = useState('');
  const [editOriginalValue, setEditOriginalValue] = useState('');
  // Id of the row currently showing a "Jump to existing" flash highlight,
  // or null when no flash is active. Reset by a timeout.
  const [flashRowId, setFlashRowId] = useState<string | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Root ref for scoping the Cmd/Ctrl+S keyboard shortcut.
  const rootRef = useRef<HTMLDivElement>(null);
  // Map of row id → row "jump target" element (the Edit button). Used by
  // the "Jump to existing" link on a duplicate-name validation error to
  // scroll to and focus the conflicting row; focusing the Edit button lets
  // the user press Enter to open the editor directly.
  const jumpTargetRefs = useRef<Map<string, HTMLElement>>(new Map());
  const registerJumpTarget = useCallback((id: string, el: HTMLElement | null) => {
    if (el) jumpTargetRefs.current.set(id, el);
    else jumpTargetRefs.current.delete(id);
  }, []);

  // Ref to avoid recreating handleSave on every items change.
  // Assigned in an effect so we don't mutate refs during render (not safe in
  // concurrent mode). Reads happen in event handlers, which run after commit.
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Sync from API → local state
  useEffect(() => {
    if (settings) {
      setItems(settingsToEditable(settings));
    }
  }, [settings]);

  // Clear the "Saved" flash timer on unmount to avoid setState-after-unmount.
  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, []);

  // Derived state
  const dirtyCount = useMemo(
    () => items.filter((item) => item.isNew || item.isDeleted || isModified(item)).length,
    [items],
  );

  const isDirty = dirtyCount > 0;

  const filteredItems = useMemo(() => {
    if (!filterText.trim()) return items;
    const lower = filterText.toLowerCase();
    return items.filter((item) => item.name.toLowerCase().includes(lower));
  }, [items, filterText]);

  // Column sizing (resizable column headers). The hook owns width state and
  // hands us per-cell prop spreaders that inject the live `style.width`.
  const { columnSizing_unstable, tableRef } = useTableFeatures(
    { columns: COLUMNS, items: filteredItems, getRowId: (item) => item.id },
    [useTableColumnSizing_unstable({ columnSizingOptions: COLUMN_SIZING })],
  );
  const getCellProps = columnSizing_unstable.getTableCellProps;
  const getHeaderCellProps = columnSizing_unstable.getTableHeaderCellProps;

  // Validation for all items — computed continuously so Save-disable is
  // always consistent. Display of field-level errors in add/edit overlays is
  // gated on `touchedNameIds` so the user isn't scolded while still typing.
  //
  // Collisions are case-sensitive: env var names are POSIX and `Foo` and
  // `foo` are two distinct variables.
  const validationErrors = useMemo<Record<string, ValidationError>>(
    () => buildValidationErrors(items),
    [items],
  );

  const errorCount = Object.keys(validationErrors).length;
  const hasValidationErrors = errorCount > 0;

  // Classify the save error to surface a clearer, actionable message.
  // See SAVE_ERROR_TITLES / SAVE_ERROR_OFFERS_RETRY at module scope for the
  // copy and retry-affordance lookups.
  const saveErrorKind = useMemo<SaveErrorKind | null>(() => {
    if (!updateMutation.isError) return null;
    const status = (updateMutation.error as ApiErrorWithStatus | undefined)?.status;
    if (status === 502 || status === 409) return 'conflict';
    if (status === 401) return 'unauthorized';
    if (status === 403) return 'forbidden';
    return 'generic';
  }, [updateMutation.isError, updateMutation.error]);

  // Handlers
  const handleAdd = useCallback(() => {
    const newItem: EditableSetting = {
      id: crypto.randomUUID(),
      name: '',
      value: '',
      isNew: true,
    };
    setItems((prev) => [newItem, ...prev]);
    setEditingId(newItem.id);
    setEditName('');
    setEditValue('');
    // No payload — env var names/values are potentially sensitive; the intent
    // (user added a blank row) is enough signal by itself.
    trackEvent('envvars.row.added');
  }, [trackEvent]);

  const handleStartEdit = useCallback((item: EditableSetting) => {
    if (item.isDeleted) return;
    setEditingId(item.id);
    setEditName(item.name);
    setEditValue(item.value);
    setEditOriginalName(item.name);
    setEditOriginalValue(item.value);
  }, []);

  const handleConfirmEdit = useCallback(() => {
    if (!editingId) return;
    setItems((prev) =>
      prev.map((item) =>
        item.id === editingId
          ? {
              ...item,
              name: editName.trim(),
              value: editValue,
            }
          : item
      )
    );
    setEditingId(null);
  }, [editingId, editName, editValue]);

  const handleCancelEdit = useCallback(() => {
    if (!editingId) return;
    const idToCancel = editingId;
    setItems((prev) => {
      const item = prev.find((i) => i.id === idToCancel);
      // Drop new rows entirely on cancel: they had no pre-existing state, so
      // whatever was typed (name, value, or both) is discarded. Prevents
      // leaving an invalid row in the table when the user bailed mid-entry.
      if (item?.isNew) {
        return prev.filter((i) => i.id !== idToCancel);
      }
      // For existing rows: restore both name AND value from the pre-edit
      // snapshot. Both fields are live-synced into `items` during editing,
      // so cancel must revert both to avoid silently retaining changes the
      // user just rejected.
      return prev.map((i) =>
        i.id === idToCancel
          ? { ...i, name: editOriginalName, value: editOriginalValue }
          : i
      );
    });
    setEditingId(null);
    // Clear touched for the cancelled row so stale errors don't flash on reopen
    setTouchedNameIds((prev) => {
      if (!prev.has(idToCancel)) return prev;
      const next = new Set(prev);
      next.delete(idToCancel);
      return next;
    });
  }, [editingId, editOriginalName, editOriginalValue]);

  const handleDelete = useCallback((id: string) => {
    // Look up the row before mutating so we can include `wasNew` in telemetry.
    const target = itemsRef.current.find((i) => i.id === id);
    setItems((prev) =>
      prev.flatMap((item) => {
        if (item.id !== id) return [item];
        // Unsaved new row — discard it entirely rather than marking for deletion.
        if (item.isNew) return [];
        return [{ ...item, isDeleted: true }];
      })
    );
    setDeletePopoverId(null);
    if (editingId === id) setEditingId(null);
    trackEvent('envvars.row.deleted', { wasNew: !!target?.isNew });
  }, [editingId, trackEvent]);

  const handleUndoDelete = useCallback((id: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isDeleted: false } : item
      )
    );
  }, []);

  const handleDiscard = useCallback(() => {
    if (settings) {
      const counts = computeChangeCounts(itemsRef.current);
      setItems(settingsToEditable(settings));
      // Counts-only — we log that the user discarded N changes but never
      // which keys or values were discarded.
      trackEvent('envvars.changes.discarded', { ...counts });
    }
    setEditingId(null);
  }, [settings, trackEvent]);

  const handleRefresh = useCallback(() => {
    if (isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    setEditingId(null);
    refetch();
  }, [isDirty, refetch]);

  const handleSave = useCallback(async () => {
    if (hasValidationErrors) return;

    // If a row is mid-edit when Save is clicked (or Cmd/Ctrl+S pressed),
    // commit the edit buffer first. The inputs live-sync name/value into
    // `items`, but the name is stored untrimmed — we want the trimmed form
    // on the wire, matching the Enter-to-confirm path.
    const committedItems = editingId
      ? itemsRef.current.map((i) =>
          i.id === editingId
            ? { ...i, name: editName.trim(), value: editValue }
            : i
        )
      : itemsRef.current;

    if (editingId) {
      setItems(committedItems);
      setEditingId(null);
    }

    const patchSettings = buildPutSettings(committedItems);

    // Empty payload is legitimate (the user deleted every setting). Skip the
    // network round-trip only when there's truly nothing pending to save.
    const counts = computeChangeCounts(committedItems);
    if (counts.total === 0) return;

    const startTime = performance.now();
    trackEvent('envvars.save.started', { ...counts });

    try {
      await updateMutation.mutateAsync({
        properties: { settings: patchSettings },
      });
      const durationMs = Math.round(performance.now() - startTime);
      trackEvent(
        'envvars.save.succeeded',
        { ...counts },
        { durationMs },
      );
      // Reset dirty-tracking state synchronously so isDirty becomes false
      // immediately — don't depend on the async React Query refetch which
      // can be skipped by structural sharing.
      setItems(resetItemsAfterSave(committedItems));
      setShowSuccess(true);
      setEditingId(null);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      const durationMs = Math.round(performance.now() - startTime);
      // Extract HTTP status + server request id if the API attached them;
      // never send the error body text — it may echo the user's own env-var
      // values back. Error.message itself is already sanitized at source.
      const apiErr = err as ApiErrorWithStatus | undefined;
      const status = apiErr?.status;
      const requestId = apiErr?.requestId;
      trackEvent(
        'envvars.save.failed',
        {
          ...counts,
          status: typeof status === 'number' ? status : 'unknown',
          ...(requestId ? { requestId } : {}),
        },
        { durationMs },
      );
      trackException(err, {
        op: 'envvars.save',
        ...(typeof status === 'number' ? { status } : {}),
        ...(requestId ? { requestId } : {}),
      });
      // UI surfaces the error via mutation state.
    }
  }, [hasValidationErrors, editingId, editName, editValue, updateMutation, trackEvent, trackException]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleConfirmEdit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancelEdit();
      }
    },
    [handleConfirmEdit, handleCancelEdit]
  );

  const handleInlineUpdate = useCallback((id: string, field: 'name' | 'value', val: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, [field]: val } : item
      )
    );
  }, []);

  // Mark a row's name input as touched so its field-level errors become
  // visible in the add/edit overlays.
  const handleNameBlur = useCallback((id: string) => {
    setTouchedNameIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  // Scroll to and focus the Edit button of the row with `id`. Used by the
  // "Jump to existing" link on a duplicate-name validation error — pressing
  // Enter on the now-focused Edit button opens the editor.
  //
  // `preventScroll: true` is essential: without it, the browser auto-scrolls
  // the newly-focused element into view using its own alignment heuristics,
  // which interrupts the smooth `scrollIntoView` animation and ends up in
  // the wrong place. A short "flash" pulses the destination row so users
  // can see where they landed (the pencil-icon focus ring alone is easy to
  // miss).
  const handleJumpTo = useCallback((id: string) => {
    const el = jumpTargetRefs.current.get(id);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    requestAnimationFrame(() => el.focus({ preventScroll: true }));
    setFlashRowId(id);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashRowId(null), ROW_FLASH_MS);
  }, []);

  // Cmd/Ctrl+S keyboard shortcut for save.
  //
  // Listener is attached at the document level but scoped to our subtree via
  // `rootRef.current.contains(document.activeElement)`. Modal dialogs (Import,
  // Delete confirm, Discard confirm) render through a portal outside the root,
  // so focus is naturally outside when a dialog owns it — we don't need to
  // explicitly check dialog state.
  //
  // We read the latest handler + gating state through a ref so the listener
  // is bound once per mount instead of re-binding on every keystroke edit.
  const saveShortcutStateRef = useRef({
    isDirty,
    hasValidationErrors,
    isPending: updateMutation.isPending,
    handleSave,
  });
  useEffect(() => {
    saveShortcutStateRef.current = {
      isDirty,
      hasValidationErrors,
      isPending: updateMutation.isPending,
      handleSave,
    };
  });
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Platform-correct save chord: ⌘S on Mac, Ctrl+S on Win/Linux.
      if (!isPrimaryMod(e)) return;
      if (e.key.toLowerCase() !== 's') return;
      // Don't hijack Ctrl+S inside an IME composition.
      if (e.isComposing) return;
      // Editor-protection: defer to code editors (CodeMirror / Monaco /
      // Lexical) which have their own Ctrl+S behavior. We DO want Cmd+S
      // to fire from ordinary inputs inside this tab (search box, row
      // edit fields) so the user can save without un-focusing first.
      const target = e.target instanceof Element ? e.target : null;
      if (
        target?.closest(
          '.cm-editor, .monaco-editor, [data-lexical-editor], [data-no-canvas-shortcuts]',
        )
      ) {
        return;
      }
      // Tab-scoping: only fire when focus is somewhere inside this Settings
      // tab. Other tabs/pages should not have their Ctrl+S stolen.
      if (!rootRef.current?.contains(document.activeElement)) return;
      // We own this key — suppress the browser Save Page dialog even on
      // held-key repeats.
      e.preventDefault();
      if (e.repeat) return;
      const { isDirty: dirty, hasValidationErrors: errors, isPending, handleSave: save } =
        saveShortcutStateRef.current;
      if (!dirty || errors || isPending) return;
      void save();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Stable callback for the row-level delete popover so the memoized row
  // doesn't re-render on every parent render.
  const handleDeletePopoverChange = useCallback((id: string, open: boolean) => {
    setDeletePopoverId(open ? id : null);
  }, []);

  const getRowClass = useCallback(
    (item: EditableSetting): string => {
      const flash = flashRowId === item.id ? styles.rowFlash : undefined;
      if (item.isDeleted) return mergeClasses(styles.row, styles.rowDeleted, flash);
      if (item.isNew) return mergeClasses(styles.row, styles.rowNew, styles.rowSlideIn, flash);
      if (editingId === item.id) return mergeClasses(styles.row, styles.rowEditing, flash);
      if (isModified(item)) return mergeClasses(styles.row, styles.rowChanged, flash);
      return mergeClasses(styles.row, flash);
    },
    [flashRowId, editingId, styles],
  );

  // Error (render early; skeletons only show while truly loading)
  if (isError) {
    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
    // Truncate long API error messages for readability
    const displayMsg = errorMsg.length > 200 ? `${errorMsg.slice(0, 200)}…` : errorMsg;
    return (
      <MessageBar intent="error" className={styles.errorBar}>
        <MessageBarBody className={styles.errorBody}>
          Failed to load environment variables: {displayMsg}
        </MessageBarBody>
      </MessageBar>
    );
  }

  const showEmptyState = !isLoading && filteredItems.length === 0 && !filterText;
  const showFilterEmpty = !isLoading && filteredItems.length === 0 && !!filterText;
  const showSkeleton = isLoading;

  return (
    <div className={styles.root} ref={rootRef}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <Input
          className={styles.searchInput}
          contentBefore={<SearchRegular />}
          placeholder="Filter settings..."
          value={filterText}
          onChange={(_, data) => setFilterText(data.value)}
          aria-label="Filter environment variables"
          autoComplete="off"
        />
        <div className={styles.toolbarActions}>
          <Button
            appearance="primary"
            icon={<AddRegular />}
            onClick={handleAdd}
          >
            Add
          </Button>
          <ToggleButton
            appearance="subtle"
            icon={showValues ? <EyeOffRegular /> : <EyeRegular />}
            checked={showValues}
            onClick={() => {
              const next = !showValues;
              // `show` is the new state — useful for understanding how
              // often users actually want to see values vs keep them masked.
              trackEvent('envvars.values.toggled', { show: next });
              setShowValues(next);
            }}
            aria-label={showValues ? 'Hide values' : 'Show values'}
          >
            {showValues ? 'Hide values' : 'Show values'}
          </ToggleButton>
          {draftSettingsEnabled && (<TagSwitch
            aria-label="Environment variables view"
            data-testid="environment-variables-view-switch"
            options={environmentViews}
            value={isDraftMode ? 'draft' : 'production'}
            onChange={(value) => {
              const draft = value === 'draft';
              trackEvent('envvars.mode.toggled', { draft });
              setIsDraftMode(draft);
            }}
          />)}
          <Button
            appearance="subtle"
            icon={isFetching ? <Spinner size="tiny" /> : <ArrowClockwiseRegular />}
            onClick={handleRefresh}
            disabled={isFetching}
            aria-label={isFetching ? 'Refreshing…' : 'Refresh'}
          />
        </div>
      </div>

      {saveErrorKind ? (
        <MessageBar
          layout="multiline"
          intent={saveErrorKind === 'conflict' ? 'warning' : 'error'}
          className={styles.statusBar}
          role="alert"
        >
          <MessageBarBody>
            <MessageBarTitle>{SAVE_ERROR_TITLES[saveErrorKind]}</MessageBarTitle>
            {saveErrorKind === 'conflict' && (
              <>
                {' '}Wait a few seconds, then try again. The container app needs to finish
                applying the previous update before it can accept another one.
              </>
            )}
            {saveErrorKind === 'generic' && updateMutation.error instanceof Error && (
              <> {updateMutation.error.message}</>
            )}
          </MessageBarBody>
          <MessageBarActions>
            {SAVE_ERROR_OFFERS_RETRY[saveErrorKind] && (
              <Button
                appearance="primary"
                size="small"
                onClick={() => {
                  updateMutation.reset();
                  void handleSave();
                }}
                disabled={updateMutation.isPending || hasValidationErrors || !isDirty}
              >
                Retry
              </Button>
            )}
            <Button
              appearance="transparent"
              size="small"
              onClick={() => updateMutation.reset()}
              aria-label="Dismiss error"
            >
              Dismiss
            </Button>
          </MessageBarActions>
        </MessageBar>
      ) : isDirty ? (
        // `isDirty` takes priority over `showSuccess`. If the user starts editing
        // again during the 3s post-save success window, we drop the green "Saved"
        // bar immediately so Save/Discard actions are visible and the new edits
        // aren't blocked behind the timer.
        <MessageBar intent="info" className={styles.statusBar} role="status">
          <MessageBarBody className={styles.statusBarBody}>
            <MessageBarTitle className={styles.statusBarCount}>
              {dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}
            </MessageBarTitle>
            {hasValidationErrors && (
              <Text size={200} className={styles.errorCount} aria-live="polite">
                {' '}— fix {errorCount} error{errorCount !== 1 ? 's' : ''} before saving
              </Text>
            )}
          </MessageBarBody>
          <MessageBarActions>
            <Button
              appearance="transparent"
              size="small"
              onClick={handleDiscard}
              disabled={updateMutation.isPending}
            >
              Discard
            </Button>
            <Button
              appearance="primary"
              size="small"
              onClick={handleSave}
              disabled={hasValidationErrors || updateMutation.isPending}
              icon={updateMutation.isPending ? <Spinner size="tiny" /> : null}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save changes'}
            </Button>
          </MessageBarActions>
        </MessageBar>
      ) : showSuccess ? (
        <MessageBar intent="success" className={styles.statusBar} role="status">
          <MessageBarBody>
            <MessageBarTitle>Saved</MessageBarTitle>
          </MessageBarBody>
        </MessageBar>
      ) : null}

      {/* Table */}
      <div className={styles.tableWrapper}>
        {showEmptyState ? (
          <div className={styles.emptyState} role="region" aria-label="No environment variables">
            <Braces48Regular className={styles.emptyIcon} aria-hidden="true" />
            <Text as="h3" size={500} weight="semibold" className={styles.emptyStateHeading}>
              No environment variables yet
            </Text>
            <Text size={300} className={styles.emptyStateBody}>
              Environment variables are available to all workflows in this app at runtime.
            </Text>
            <div className={styles.emptyStateActions}>
              <Button appearance="primary" icon={<AddRegular />} onClick={handleAdd}>
                Add variable
              </Button>
            </div>
          </div>
        ) : showFilterEmpty ? (
          <div className={styles.emptyState}>
            <SearchRegular className={styles.emptyIcon} />
            <Text size={400}>No variables match &ldquo;{filterText}&rdquo;</Text>
          </div>
        ) : (
          <Table
            // Fluent's `<Table>` is polymorphic and its default ref type
            // resolves to `Ref<never>`. The column-sizing hook needs the
            // actual table element ref to position the drag handles, so
            // we cast — the runtime works fine, only the types disagree.
            ref={tableRef as unknown as React.Ref<never>}
            className={styles.table}
            aria-label="Environment variables"
            size="medium"
            aria-busy={showSkeleton || undefined}
          >
            <TableHeader>
              <TableRow className={styles.headerRow}>
                <TableHeaderCell
                  {...getHeaderCellProps('name')}
                  className={styles.headerCell}
                >
                  Name
                </TableHeaderCell>
                <TableHeaderCell
                  {...getHeaderCellProps('value')}
                  className={styles.headerCell}
                >
                  Value
                </TableHeaderCell>
                <TableHeaderCell
                  {...getHeaderCellProps('actions')}
                  className={mergeClasses(styles.headerCell, styles.actionsColumn)}
                >
                  Actions
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {showSkeleton
                ? Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={`skeleton-${i}`} aria-label="Loading environment variables..." className={styles.row}>
                      <TableCell {...getCellProps('name')} className={styles.skeletonCell}>
                        <Skeleton>
                          <SkeletonItem
                            className={mergeClasses(styles.skeletonItem, styles.skeletonItemShort)}
                          />
                        </Skeleton>
                      </TableCell>
                      <TableCell {...getCellProps('value')} className={styles.skeletonCell}>
                        <Skeleton>
                          <SkeletonItem
                            className={mergeClasses(styles.skeletonItem, styles.skeletonItemLong)}
                          />
                        </Skeleton>
                      </TableCell>
                      <TableCell {...getCellProps('actions')} className={styles.skeletonCell}>
                        <Skeleton>
                          <SkeletonItem
                            className={mergeClasses(styles.skeletonItem, styles.skeletonItemActions)}
                          />
                        </Skeleton>
                      </TableCell>
                    </TableRow>
                  ))
                : filteredItems.map((item) => (
                    <EnvVarRow
                      key={item.id}
                      item={item}
                      isEditing={editingId === item.id}
                      editName={editName}
                      editValue={editValue}
                      showValues={showValues}
                      validationError={validationErrors[item.id]}
                      showFieldError={touchedNameIds.has(item.id)}
                      deletePopoverOpen={deletePopoverId === item.id}
                      rowClass={getRowClass(item)}
                      getCellProps={getCellProps}
                      onSetEditName={setEditName}
                      onSetEditValue={setEditValue}
                      onStartEdit={handleStartEdit}
                      onConfirmEdit={handleConfirmEdit}
                      onCancelEdit={handleCancelEdit}
                      onDelete={handleDelete}
                      onUndoDelete={handleUndoDelete}
                      onKeyDown={handleKeyDown}
                      onInlineUpdate={handleInlineUpdate}
                      onNameBlur={handleNameBlur}
                      onJumpTo={handleJumpTo}
                      onRegisterJumpTarget={registerJumpTarget}
                      onDeletePopoverChange={handleDeletePopoverChange}
                    />
                  ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog
        open={discardConfirmOpen}
        onOpenChange={(_, data) => setDiscardConfirmOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Discard unsaved changes?</DialogTitle>
            <DialogContent>
              You have unsaved changes. Refreshing will discard them. Continue?
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                className={styles.dialogButton}
                onClick={() => setDiscardConfirmOpen(false)}
              >
                Keep editing
              </Button>
              <Button
                appearance="primary"
                className={styles.dialogButton}
                onClick={() => {
                  setEditingId(null);
                  refetch();
                  setDiscardConfirmOpen(false);
                }}
              >
                Discard and refresh
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

EnvironmentVariablesTab.displayName = 'EnvironmentVariablesTab';

// =============================================================================
// ROW SUB-COMPONENT
// =============================================================================

/** "Jump to existing" affordance for duplicate-name validation errors. */
const DuplicateJumpLink = ({
  validationError,
  onJumpTo,
  className,
}: {
  validationError: ValidationError;
  onJumpTo: (id: string) => void;
  className?: string;
}) => {
  if (validationError.kind !== 'duplicate' || !validationError.duplicateOfId) return null;
  const targetId = validationError.duplicateOfId;
  return (
    <Link as="button" className={className} onClick={() => onJumpTo(targetId)}>
      Jump to existing
    </Link>
  );
};

/**
 * Name-field editor used by both the new-row and edit-existing-row branches:
 * a Fluent `<Field>` (with hint + error message rendering) wrapping an Input,
 * plus the optional "Jump to existing" link for duplicate errors.
 */
const NameFieldEditor = ({
  value,
  ariaLabel,
  placeholder,
  validationError,
  showError,
  onChange,
  onBlur,
  onKeyDown,
  onJumpTo,
}: {
  value: string;
  ariaLabel: string;
  placeholder?: string;
  validationError: ValidationError | undefined;
  showError: boolean;
  onChange: (value: string) => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onJumpTo: (id: string) => void;
}) => {
  const styles = useEnvironmentVariablesTabStyles();
  const showErr = showError && !!validationError;
  return (
    <div className={styles.nameFieldGroup}>
      <Field
        hint={POSIX_KEY_HINT}
        {...(showErr
          ? { validationState: 'error' as const, validationMessage: validationError.message }
          : {})}
      >
        <Input
          className={styles.cellInput}
          {...(placeholder !== undefined ? { placeholder } : {})}
          value={value}
          onChange={(_, data) => onChange(data.value)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          aria-label={ariaLabel}
          autoComplete="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          autoFocus
        />
      </Field>
      {showErr && (
        <DuplicateJumpLink
          validationError={validationError}
          onJumpTo={onJumpTo}
          className={styles.jumpLink}
        />
      )}
    </div>
  );
};

interface EnvVarRowProps {
  item: EditableSetting;
  isEditing: boolean;
  editName: string;
  editValue: string;
  showValues: boolean;
  validationError: ValidationError | undefined;
  // Whether to display field-level validation in the add/edit overlays.
  // View-mode rows always display their error regardless of this flag.
  showFieldError: boolean;
  deletePopoverOpen: boolean;
  rowClass: string;
  /**
   * Returns column-sizing props (currently `{ style: { width } }`) to spread
   * on each `TableCell`. Sourced from `useTableColumnSizing_unstable` so cell
   * widths stay in sync with the draggable header handles.
   */
  getCellProps: (columnId: EnvColumnId) => { style?: React.CSSProperties | undefined };
  onSetEditName: (v: string) => void;
  onSetEditValue: (v: string) => void;
  onStartEdit: (item: EditableSetting) => void;
  onConfirmEdit: () => void;
  onCancelEdit: () => void;
  onDelete: (id: string) => void;
  onUndoDelete: (id: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onInlineUpdate: (id: string, field: 'name' | 'value', val: string) => void;
  onNameBlur: (id: string) => void;
  onJumpTo: (id: string) => void;
  /**
   * Registers the row's "jump target" element (the Edit button) for the
   * "Jump to existing" duplicate-name validation flow. Focusing the Edit
   * button brings the user to the right row and lets them press Enter to
   * start editing — same end result as the old click-text-to-edit flow.
   */
  onRegisterJumpTarget: (id: string, el: HTMLElement | null) => void;
  onDeletePopoverChange: (id: string, open: boolean) => void;
}

const EnvVarRow = memo(({
  item,
  isEditing,
  editName,
  editValue,
  showValues,
  validationError,
  showFieldError,
  deletePopoverOpen,
  rowClass,
  getCellProps,
  onSetEditName,
  onSetEditValue,
  onStartEdit,
  onConfirmEdit,
  onCancelEdit,
  onDelete,
  onUndoDelete,
  onKeyDown,
  onInlineUpdate,
  onNameBlur,
  onJumpTo,
  onRegisterJumpTarget,
  onDeletePopoverChange,
}: EnvVarRowProps) => {
  const styles = useEnvironmentVariablesTabStyles();
  const { trackEvent } = useTelemetry();
  const [rowOverride, setRowOverride] = useState(false);
  // Which copy button most recently succeeded — drives the per-button
  // Copy → Checkmark flip and SR-only "Copied!" announcement.
  const [copiedTarget, setCopiedTarget] = useState<'reference' | 'value' | null>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset per-row override when the global toggle changes
  useEffect(() => {
    setRowOverride(false);
  }, [showValues]);

  // Clean up the copy-flash timer on unmount
  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const flashCopied = useCallback((target: 'reference' | 'value') => {
    setCopiedTarget(target);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopiedTarget(null), COPIED_FLASH_MS);
  }, []);

  const handleCopyReference = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(formatReference(item.name));
      flashCopied('reference');
      // No payload: the reference format is a template, and emitting the
      // actual key would leak potentially sensitive identifiers.
      trackEvent('envvars.copy.reference');
    } catch {
      // Clipboard access can fail (permissions, insecure context). We don't
      // escalate to a toast — a silent no-op keeps the UI quiet and the user
      // can always copy the displayed name manually.
    }
  }, [item.name, flashCopied, trackEvent]);

  const handleCopyValue = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(item.value);
      flashCopied('value');
      // No payload: never log values.
      trackEvent('envvars.copy.value');
    } catch {
      // Same rationale as handleCopyReference — silent fall-back.
    }
  }, [item.value, flashCopied, trackEvent]);

  // Global OFF + row override ON → show (opt-in)
  // Global ON  + row override ON → hide (opt-out)
  const valueVisible = showValues ? !rowOverride : rowOverride;

  // For new inline rows (not in edit-overlay mode)
  if (item.isNew && isEditing) {
    return (
      <TableRow className={rowClass}>
        <TableCell {...getCellProps('name')} className={mergeClasses(styles.cell, styles.editingCell)}>
          <NameFieldEditor
            value={editName}
            ariaLabel="New variable name"
            placeholder="Variable name"
            validationError={validationError}
            showError={showFieldError}
            onChange={(v) => {
              onSetEditName(v);
              onInlineUpdate(item.id, 'name', v);
            }}
            onBlur={() => onNameBlur(item.id)}
            onKeyDown={onKeyDown}
            onJumpTo={onJumpTo}
          />
        </TableCell>
        <TableCell {...getCellProps('value')} className={mergeClasses(styles.cell, styles.editingCell)}>
          <Input
            className={styles.cellInput}
            placeholder="Value"
            type={valueVisible ? 'text' : 'password'}
            value={editValue}
            onChange={(_, data) => {
              onSetEditValue(data.value);
              onInlineUpdate(item.id, 'value', data.value);
            }}
            onKeyDown={onKeyDown}
            aria-label="New variable value"
            autoComplete="off"
            spellCheck={false}
            data-1p-ignore
            data-lpignore="true"
          />
        </TableCell>
        <TableCell {...getCellProps('actions')} className={mergeClasses(styles.cell, styles.editingCell)}>
          <div className={styles.actionsCell}>
            <Button
              appearance="subtle"
              icon={<CheckmarkRegular />}
              size="small"
              onClick={onConfirmEdit}
              aria-label="Confirm"
            />
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              size="small"
              onClick={onCancelEdit}
              aria-label="Cancel"
            />
          </div>
        </TableCell>
      </TableRow>
    );
  }

  // Editing existing row
  if (isEditing) {
    return (
      <TableRow className={rowClass}>
        <TableCell {...getCellProps('name')} className={mergeClasses(styles.cell, styles.editingCell)}>
          <NameFieldEditor
            value={editName}
            ariaLabel="Edit variable name"
            validationError={validationError}
            showError={showFieldError}
            onChange={(v) => {
              onSetEditName(v);
              // Live-sync the name so validation (duplicate/regex) runs against
              // the candidate value. `handleCancelEdit` restores the original
              // name if the user bails out.
              onInlineUpdate(item.id, 'name', v);
            }}
            onBlur={() => onNameBlur(item.id)}
            onKeyDown={onKeyDown}
            onJumpTo={onJumpTo}
          />
        </TableCell>
        <TableCell {...getCellProps('value')} className={mergeClasses(styles.cell, styles.editingCell)}>
          <div className={styles.valueCell}>
            <Input
              className={styles.cellInput}
              type={valueVisible ? 'text' : 'password'}
              value={editValue}
              onChange={(_, data) => onSetEditValue(data.value)}
              onKeyDown={onKeyDown}
              aria-label="Edit variable value"
              autoComplete="off"
              spellCheck={false}
              data-1p-ignore
              data-lpignore="true"
            />
            <Button
              appearance="subtle"
              icon={valueVisible ? <EyeOffRegular /> : <EyeRegular />}
              size="small"
              onClick={() => setRowOverride((v) => !v)}
              aria-label={valueVisible ? 'Hide value' : 'Show value'}
            />
          </div>
        </TableCell>
        <TableCell {...getCellProps('actions')} className={mergeClasses(styles.cell, styles.editingCell)}>
          <div className={styles.actionsCell}>
            <Button
              appearance="subtle"
              icon={<CheckmarkRegular />}
              size="small"
              onClick={onConfirmEdit}
              aria-label="Confirm edit"
            />
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              size="small"
              onClick={onCancelEdit}
              aria-label="Cancel edit"
            />
          </div>
        </TableCell>
      </TableRow>
    );
  }

  // View mode
  const copiedReference = copiedTarget === 'reference';
  const copiedValue = copiedTarget === 'value';
  // Copy works against the underlying value, so it's available even when the
  // user has kept the value masked — they shouldn't have to reveal a secret
  // on-screen just to put it on the clipboard.
  const canCopyValue = !item.isDeleted && !!item.value;

  return (
    <TableRow className={rowClass}>
      <TableCell {...getCellProps('name')} className={styles.cell}>
        <div className={styles.valueCell}>
          <Text className={mergeClasses(styles.cellText, styles.valueText)}>
            {item.name || <span className={styles.emptyText}>(empty)</span>}
          </Text>
          {!item.isDeleted && !item.isNew && item.name && (
            <Tooltip
              content={copiedReference ? 'Copied!' : `Copy reference ${formatReference(item.name)}`}
              relationship="label"
            >
              <Button
                appearance="subtle"
                size="small"
                className={styles.copyButton}
                data-hover-reveal=""
                icon={copiedReference ? <CheckmarkRegular /> : <CopyRegular />}
                onClick={handleCopyReference}
                aria-label={`Copy reference to ${item.name}`}
              />
            </Tooltip>
          )}
          {copiedReference && (
            <span className={styles.srOnly} role="status" aria-live="polite">
              Copied reference to {item.name}
            </span>
          )}
        </div>
        {validationError && (
          <div className={styles.errorGroup}>
            {/* Match Fluent `<Field validationMessage>` visual so view-mode
                errors look identical to edit-mode errors: small filled
                error-circle icon + red text at fontSizeBase200. */}
            <span className={styles.validationError} role="alert">
              <ErrorCircle12Filled aria-hidden="true" className={styles.validationErrorIcon} />
              {validationError.message}
            </span>
            <DuplicateJumpLink
              validationError={validationError}
              onJumpTo={onJumpTo}
              className={styles.jumpLink}
            />
          </div>
        )}
      </TableCell>
      <TableCell {...getCellProps('value')} className={styles.cell}>
        <div className={styles.valueCell}>
          <Text className={mergeClasses(styles.cellText, styles.valueText, !valueVisible && styles.maskedText)}>
            {valueVisible
              ? item.value || <span className={styles.emptyText}>(empty)</span>
              : MASK}
          </Text>
          {/*
            View-mode action order is (text)(copy)(eye):
              • [text…………………] [copy] [eye]
            Copy is always shown when there's a value to copy — even while
            masked — so users don't have to reveal a secret on-screen just
            to put it on the clipboard. The eye toggle stays anchored at
            the right edge of the cell.
          */}
          {canCopyValue && (
            <Tooltip
              content={copiedValue ? 'Copied!' : 'Copy value'}
              relationship="label"
            >
              <Button
                appearance="subtle"
                size="small"
                className={styles.copyButton}
                data-hover-reveal=""
                icon={copiedValue ? <CheckmarkRegular /> : <CopyRegular />}
                onClick={handleCopyValue}
                aria-label={`Copy value of ${item.name}`}
              />
            </Tooltip>
          )}
          {!item.isDeleted && (
            <Button
              appearance="subtle"
              icon={valueVisible ? <EyeOffRegular /> : <EyeRegular />}
              size="small"
              className={styles.rowActionButton}
              onClick={() => setRowOverride((v) => !v)}
              aria-label={valueVisible ? 'Hide value' : 'Show value'}
              aria-pressed={valueVisible}
            />
          )}
          {copiedValue && (
            <span className={styles.srOnly} role="status" aria-live="polite">
              Value copied to clipboard
            </span>
          )}
        </div>
      </TableCell>
      <TableCell {...getCellProps('actions')} className={styles.cell}>
        <div className={styles.actionsCell}>
          {item.isDeleted ? (
            <Button
              appearance="subtle"
              size="small"
              className={styles.rowActionButton}
              onClick={() => onUndoDelete(item.id)}
              aria-label="Undo delete"
            >
              Undo
            </Button>
          ) : (
            <>
              <Button
                appearance="subtle"
                icon={<EditRegular />}
                size="small"
                className={styles.rowActionButton}
                onClick={() => onStartEdit(item)}
                aria-label={`Edit ${item.name}`}
                ref={(el: HTMLButtonElement | null) => onRegisterJumpTarget(item.id, el)}
              />
              <Popover
                open={deletePopoverOpen}
                onOpenChange={(_, data) => onDeletePopoverChange(item.id, data.open)}
              >
                <PopoverTrigger disableButtonEnhancement>
                  <Button
                    appearance="subtle"
                    icon={<DeleteRegular />}
                    size="small"
                    className={styles.rowActionButton}
                    aria-label={`Delete ${item.name}`}
                  />
                </PopoverTrigger>
                <PopoverSurface>
                  <div className={styles.popoverContent}>
                    <Text weight="semibold">Delete &ldquo;{item.name}&rdquo;?</Text>
                    <Text size={200}>This variable will be removed when you save.</Text>
                    <div className={styles.popoverActions}>
                      <Button
                        appearance="subtle"
                        size="small"
                        onClick={() => onDeletePopoverChange(item.id, false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        appearance="primary"
                        size="small"
                        onClick={() => onDelete(item.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </PopoverSurface>
              </Popover>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
});

EnvVarRow.displayName = 'EnvVarRow';
